import { teamsClient } from '@atlaskit/teams-client';

import { type TeamsPermissions } from '../../hooks/use-team-visibility';
import type { StoreApi, TeamAppState as TeamState } from '../../types';

export const removeAgentState = (
	teamState: TeamState,
	memberId: string,
	_permissions?: Partial<TeamsPermissions>,
): TeamState => {
	const { teamProfile, teamAgents } = teamState;
	const teamId = teamProfile.teamId;

	const finalAgents = {
		...teamAgents,
		teamId,
		hasLoaded: true,
		hasError: false,
		isLoading: false,
		errorType: null,
		members: teamAgents.members.filter((agent) => !!agent.user && agent.user.id !== memberId),
	};

	return {
		...teamState,
		teamAgents: finalAgents,
	};
};

export const removeAgentAssociationState = (teamState: TeamState, agentId: string): TeamState => {
	const { teamProfile, teamAgents } = teamState;
	const teamId = teamProfile.teamId;

	const finalAgents = {
		...teamAgents,
		teamId,
		hasLoaded: true,
		hasError: false,
		isLoading: false,
		errorType: null,
		agents: teamAgents.agents.filter(({ associationId }) => associationId.accountId !== agentId),
	};

	return {
		...teamState,
		teamAgents: finalAgents,
	};
};

export const removeTeamAgent =
	(teamId: string, agentAtlassianAccountId: string, permissions?: Partial<TeamsPermissions>) =>
	async ({ getState, setState }: StoreApi): Promise<void> => {
		const state = getState();
		const removeMembershipResponse = await teamsClient.removeTeamMemberships(teamId, [
			{
				accountId: agentAtlassianAccountId,
			},
		]);

		// as we only removing 1 member at a time, it is safe to say task failed with even 1 error
		if (removeMembershipResponse.errors && removeMembershipResponse.errors.length) {
			throw Error('Membership claims to be removed but was not');
		}

		if (state.teamMembers.teamId !== teamId) {
			return;
		}

		const nextState = removeAgentState(state, agentAtlassianAccountId, permissions);
		setState(nextState);
	};

export const removeTeamAgentAssociation =
	(
		teamId: string,
		agentId: string,
		onRemoveAgentSuccess?: (agentId: string, teamId: string) => void,
	) =>
	async ({ getState, setState }: StoreApi): Promise<void> => {
		const state = getState();
		await teamsClient.removeAgentTeamAssociation(teamId, agentId);

		onRemoveAgentSuccess?.(agentId, teamId);

		if (state.teamMembers.teamId !== teamId) {
			return;
		}

		const nextState = removeAgentAssociationState(state, agentId);
		setState(nextState);
	};
